/**
 * monday 的调色板。
 *
 * 注意：`BoardGroup.Color` 与 `Board.BoardColor` 在库里存的是**色名**（如 'bright-blue'），
 * 不是 hex；只有列设置里的 `color_mapping` 才直接存 hex。
 *
 * 色值以 MONDAY-CLONE-SPEC.md §7.1 为准（之前几处取自别处、不准，已更正）：
 *   dark-pink #ff007f（不是 #ff158a）· red-shadow #df2f4a（不是 #e2445c）
 *   purple #9d50dd · dark-orange #ff6d3b · blue-links #007eb5
 */
export const PALETTE: Record<string, string> = {
  'bright-blue': '#579bfc',
  'blue-links': '#007eb5',
  'green-shadow': '#00c875',
  'grass-green': '#037f4c',
  'lime-green': '#9cd326',
  'red-shadow': '#df2f4a',
  'dark-red': '#bb3354',
  'orange': '#fdab3d',
  'dark-orange': '#ff6d3b',
  'yellow': '#ffcb00',
  'mustered': '#cab641',
  'purple': '#9d50dd',
  'dark-purple': '#784bd1',
  'dark-pink': '#ff007f',
  'light-pink': '#ff5ac4',
  'turquoise': '#66ccff',
  'grey': '#c4c4c4',
  'trolley-grey': '#757575',
  'brown': '#7f5347',
  'soft-black': '#333333',
}

/**
 * 旧数据别名：早期导入 monday 时用过 blue/dark-green/red/pink/lime 这类短名，
 * palette 里没有这些键 → colorOf 回落到灰色（表现为"分组没颜色"）。
 * 这里做一次别名映射，老数据不用迁移也能正常显示。
 */
const LEGACY_ALIAS: Record<string, string> = {
  blue: 'bright-blue',
  'dark-blue': 'blue-links',
  green: 'green-shadow',
  'dark-green': 'grass-green',
  lime: 'lime-green',
  red: 'red-shadow',
  'dark-red': 'dark-red',
  pink: 'light-pink',
  'dark-pink': 'dark-pink',
  orange: 'orange',
  yellow: 'yellow',
  purple: 'purple',
  'dark-purple': 'dark-purple',
  grey: 'grey',
  gray: 'grey',
  'dark-grey': 'trolley-grey',
  'dark-gray': 'trolley-grey',
}

const FALLBACK = '#c4c4c4'

/** 色名 -> hex；未知色名回落到灰 */
export function colorOf(name: string): string {
  if (name in PALETTE) return PALETTE[name] as string
  const alias = LEGACY_ALIAS[name]
  if (alias !== undefined && alias in PALETTE) return PALETTE[alias] as string
  return FALLBACK
}

/**
 * 新分组的默认色：优先挑一个当前还没被用过的色名，都用过才回绕。
 *
 * 之前新建分组一律 bright-blue（服务端写死的默认值），几个新组看起来一模一样。
 * 选色器本身属于 M5，但「默认色恒定」是取值不当，这里先修掉。
 */
export function nextGroupColor(used: readonly string[]): string {
  const names = Object.keys(PALETTE)
  const taken = new Set(used)
  for (const name of names) {
    if (!taken.has(name)) return name
  }
  return names[used.length % names.length] ?? 'bright-blue'
}

/**
 * 分组颜色选择器的 18 个色格（MONDAY-CLONE-SPEC.md §7.2 实测顺序，每行 5 个）。
 * 注意：常量表里的 `#784bd1`（dark-purple）**不在这 18 格里**，所以这里不列它。
 * 库里存的是色名而不是 hex，所以这里用名字。
 */
export const GROUP_COLOR_NAMES = [
  'grass-green', 'green-shadow', 'lime-green', 'mustered', 'yellow',
  'purple', 'blue-links', 'bright-blue', 'turquoise', 'dark-red',
  'red-shadow', 'dark-pink', 'light-pink', 'dark-orange', 'orange',
  'brown', 'grey', 'trolley-grey',
] as const